import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import '../App.css';

// Shown as money, from the cents the plans are priced in.
// Written the way a price is read rather than the way it is stored: the symbol in front, and the
// currency code left off, because a reader paying in dollars does not need telling they are dollars.
const c_CurrencySymbols = {
	USD: '$',
	CAD: '$'
};

const format_amount = (amount_in_cents, currency) => {
	const amount = ((amount_in_cents ? amount_in_cents : 0) / 100).toFixed(2);
	const symbol = (currency && c_CurrencySymbols[currency]) ? c_CurrencySymbols[currency] : '';

	return symbol + amount;
};

// How often the ongoing charge falls, said the way a sentence needs it rather than as the stored
// value, which is the gateway's word and not something to put in front of a reader.
const get_interval_word = (billing_interval) => {
	if(billing_interval === 'year') {
		return 'a year';
	}

	return 'a month';
};

const format_date = (value) => {
	if(!value) {
		return '';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// What the change does, said before it is made. An upgrade names the amount and says it is charged
// now; a downgrade names the date and says nothing is charged.
const render_preview_body = (preview) => {
	if(preview.change_type === 'upgrade') {
		return (
			<React.Fragment>
				<p className="plan-change-summary">
					Moving from {preview.current_plan_name} to {preview.new_plan_name}.
				</p>
				<p className="plan-change-summary">
					Your card will be charged {format_amount(preview.amount_due_now, preview.currency)} today,
					then {format_amount(preview.recurring_amount, preview.currency)} every month after.
				</p>
				<p className="plan-change-note">
					Your extra credits arrive straight away.
				</p>
			</React.Fragment>
		);
	}

	// Named as a date when there is one, and as the event otherwise, so the sentence reads properly
	// either way rather than trailing off where the day should be.
	const effective_date = format_date(preview.effective_date);
	const until = effective_date ? ('until ' + effective_date) : 'until your next renewal';

	return (
		<React.Fragment>
			<p className="plan-change-summary">
				Moving from {preview.current_plan_name} to {preview.new_plan_name}.
			</p>
			<p className="plan-change-summary">
				Nothing is charged and nothing changes today. You keep the {preview.current_plan_name} plan
				and its credits {until}, when the {preview.new_plan_name} plan starts.
			</p>
			<p className="plan-change-note">
				There is no refund for the rest of this period, because you keep what you paid for
				until it runs out.
			</p>
		</React.Fragment>
	);
};

function PlanChangeModal({ show, plan, user_id, token, on_hide, on_changed }) {
	const [preview, set_preview] = useState(null);
	const [loading, set_loading] = useState(false);
	const [submitting, set_submitting] = useState(false);
	const [error, set_error] = useState('');

	// Read every time the modal is opened for a plan, rather than once, so the amount shown is
	// worked out against how much of the period is actually left at the moment of asking.
	useEffect(function() {
		if(!show || !plan || !user_id || !token) {
			return;
		}

		set_loading(true);
		set_error('');
		set_preview(null);

		const headers = { Authorization: 'Bearer ' + token };
		const url = Constants.SERVER_URL + '/api/plan_change_preview?plan_id=' + plan.id;

		axios.get(url, { headers })
			.then(function(response) {
				set_preview(response.data);
				set_loading(false);
			})
			.catch(function(error) {
				console.log('PlanChangeModal.load_preview - ERROR: ', error);
				const message = error.response && error.response.data && error.response.data.message;
				set_error(message || 'Could not work out what this change would cost.');
				set_loading(false);
			});
	}, [show, plan, user_id, token]);

	const handle_confirm = async () => {
		if(!preview) {
			return;
		}

		set_submitting(true);
		set_error('');

		try {
			const headers = { Authorization: 'Bearer ' + token };
			const path = (preview.change_type === 'upgrade') ? '/api/upgrade_subscription' : '/api/schedule_downgrade';

			await axios.post(Constants.SERVER_URL + path, { plan_id: plan.id }, { headers });

			set_submitting(false);
			on_changed();
		} catch (error) {
			console.log('PlanChangeModal.handle_confirm - ERROR: ', error);
			const message = error.response && error.response.data && error.response.data.message;
			set_error(message || 'The change could not be made. Please try again.');
			set_submitting(false);
		}
	};

	const render_body = () => {
		if(loading) {
			return <Spinner animation="border" size="sm" />;
		}

		if(!preview) {
			return null;
		}

		return (
			<React.Fragment>
				{render_preview_body(preview)}
				{!preview.allowed && (
					<Alert variant="warning">
						You have already upgraded during this billing period. You can upgrade again
						from {format_date(preview.unlocks_at)}.
					</Alert>
				)}
			</React.Fragment>
		);
	};

	const is_blocked = preview ? (preview.allowed === false) : false;
	const confirm_label = (preview && preview.change_type === 'upgrade') ? 'Upgrade Now' : 'Schedule Change';

	return (
		<Modal show={show} onHide={on_hide} centered>
			<Modal.Header closeButton>
				<Modal.Title>{plan ? ('Change to ' + plan.name) : 'Change Plan'}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{render_body()}
				{error && <Alert variant="danger">{error}</Alert>}
			</Modal.Body>

			<Modal.Footer>
				<Button variant="secondary" onClick={on_hide} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handle_confirm}
					disabled={submitting || loading || is_blocked || !preview}>
					{submitting ? <Spinner size="sm" animation="border" /> : confirm_label}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default PlanChangeModal;
